/**
 * LayoutShell — Modern Enterprise Application Shell
 *
 * Clean, minimal layout with smooth transitions.
 * Light sidebar on light background, frosted glass header.
 */

import React, { useEffect, useMemo, memo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, Drawer, useMediaQuery, useTheme } from '@mui/material';
import { useDesignSystemStore } from '../store/useDesignSystemStore';
import {
  TOPBAR_HEIGHT,
  SIDEBAR_EXPANDED,
  SIDEBAR_COLLAPSED,
  BP_MOBILE,
  BP_LAPTOP,
} from '../theme/tokens';
import TopHeader from './TopHeader';
import Sidebar from './Sidebar';
import BreadcrumbBar from './BreadcrumbBar';
import Footer from './Footer';
import SkipNavLink from './SkipNavLink';
import RouteAnnouncer from './RouteAnnouncer';
import MobileBottomNav, { BOTTOM_NAV_HEIGHT } from './MobileBottomNav';
import MobileHeader from './MobileHeader';
import ProfileCompletionBanner from '../../components/ProfileCompletionBanner';
import WelcomeKitBanner from '../../components/WelcomeKitBanner';
import UnaddressedRequestsBanner from '../../components/UnaddressedRequestsBanner';
import ActiveRemoteSession from '../../components/RemoteSupport/ActiveRemoteSession';

// ── Transition config ─────────────────────────────────────────────────────────
const SIDEBAR_TRANSITION = 'width 200ms cubic-bezier(0.4, 0, 0.2, 1)';
const MARGIN_TRANSITION = 'margin-left 200ms cubic-bezier(0.4, 0, 0.2, 1)';

// ── Memoized Content Area ─────────────────────────────────────────────────────
const ContentArea = memo(function ContentArea() {
  return (
    <Box sx={{ flex: 1, p: { xs: 1.5, sm: 2.5, md: 3 }, maxWidth: '1600px', width: '100%', mx: 'auto' }}>
      <Outlet />
    </Box>
  );
});

// ── Main Layout Shell ─────────────────────────────────────────────────────────
function LayoutShell() {
  const theme = useTheme();
  const location = useLocation();

  const sidebarCollapsed = useDesignSystemStore((s) => s.sidebarCollapsed);
  const mobileDrawerOpen = useDesignSystemStore((s) => s.mobileDrawerOpen);
  const setSidebarCollapsed = useDesignSystemStore((s) => s.setSidebarCollapsed);
  const setMobileDrawerOpen = useDesignSystemStore((s) => s.setMobileDrawerOpen);
  const addRecentPage = useDesignSystemStore((s) => s.addRecentPage);

  const isMobile = useMediaQuery(`(max-width:${BP_MOBILE - 1}px)`);
  const isTablet = useMediaQuery(
    `(min-width:${BP_MOBILE}px) and (max-width:${BP_LAPTOP - 1}px)`
  );

  useEffect(() => {
    if (isTablet && !sidebarCollapsed) {
      setSidebarCollapsed(true);
    }
  }, [isTablet]);

  useEffect(() => {
    if (!isMobile && mobileDrawerOpen) {
      setMobileDrawerOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    const path = location.pathname;
    if (
      path === '/' ||
      path === '/login' ||
      path === '/forgot-password' ||
      path === '/reset-password' ||
      path === '/accept-invite' ||
      path === '/unauthorized'
    ) return;

    const segments = path.split('/').filter(Boolean);
    const lastSeg = segments[segments.length - 1] || '';
    if (/^[a-f0-9]{24}$|^[0-9a-f-]{36}$/i.test(lastSeg)) return;

    const label = lastSeg
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');

    addRecentPage(path, label);
  }, [location.pathname]);

  const sidebarWidth = useMemo(
    () => (sidebarCollapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED),
    [sidebarCollapsed]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default', overflow: 'hidden' }}>
      <SkipNavLink />
      <RouteAnnouncer />

      {/* ─── Header (different for mobile vs desktop) ───────────── */}
      {isMobile ? <MobileHeader /> : <TopHeader />}

      {/* ─── Body ───────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', flex: 1, pt: `${TOPBAR_HEIGHT}px`, overflow: 'hidden' }}>
        {/* Desktop/Tablet Sidebar — NOT shown on mobile */}
        {!isMobile && (
          <Box
            component="aside"
            aria-label="Side navigation"
            sx={{
              position: 'fixed',
              top: TOPBAR_HEIGHT,
              left: 0,
              bottom: 0,
              width: sidebarWidth,
              transition: SIDEBAR_TRANSITION,
              zIndex: theme.zIndex.drawer,
              overflowX: 'hidden',
              willChange: 'width',
            }}
          >
            <Sidebar />
          </Box>
        )}

        {/* Mobile Drawer — keep for "More" button in bottom nav */}
        {isMobile && (
          <Drawer
            variant="temporary"
            open={mobileDrawerOpen}
            onClose={() => setMobileDrawerOpen(false)}
            ModalProps={{ keepMounted: true }}
            anchor="bottom"
            sx={{
              '& .MuiDrawer-paper': {
                width: '100%',
                maxHeight: '70vh',
                borderTopLeftRadius: 20,
                borderTopRightRadius: 20,
                border: 'none',
                boxShadow: '0 -8px 32px rgba(0,0,0,0.15)',
                overflow: 'hidden',
              },
              '& .MuiBackdrop-root': {
                backgroundColor: 'rgba(15, 23, 42, 0.4)',
                backdropFilter: 'blur(4px)',
              },
            }}
          >
            {/* Drag handle — tap to close */}
            <Box
              onClick={() => setMobileDrawerOpen(false)}
              sx={{ display: 'flex', justifyContent: 'center', py: 1.5, cursor: 'pointer' }}
            >
              <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: 'divider' }} />
            </Box>
            <Box sx={{ overflow: 'auto', maxHeight: 'calc(70vh - 40px)' }}>
              <Sidebar />
            </Box>
          </Drawer>
        )}

        {/* ─── Main Content ─────────────────────────────────────── */}
        <Box
          component="main"
          id="main-content"
          tabIndex={-1}
          sx={{
            flex: 1,
            ml: isMobile ? 0 : `${sidebarWidth}px`,
            transition: isMobile ? 'none' : MARGIN_TRANSITION,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            bgcolor: 'background.default',
            width: isMobile ? '100%' : `calc(100% - ${sidebarWidth}px)`,
            willChange: 'margin-left',
            overflowY: 'auto',
            // Add bottom padding on mobile for the bottom nav bar
            pb: isMobile ? `${BOTTOM_NAV_HEIGHT + 16}px` : 0,
          }}
        >
          {/* Profile Completion Banner */}
          <ProfileCompletionBanner />

          {/* Welcome Kit / Policy Acceptance Banner */}
          <WelcomeKitBanner />

          {/* Unaddressed service-desk requests heads-up */}
          <UnaddressedRequestsBanner />

          {/* Breadcrumb — NOT shown on mobile (uses back button instead) */}
          {!isMobile && (
            <Box sx={{ px: { xs: 2, sm: 2.5, md: 3 }, pt: 1.5, pb: 0.5, maxWidth: '1600px', width: '100%', mx: 'auto' }}>
              <BreadcrumbBar />
            </Box>
          )}

          {/* Page Content */}
          <ContentArea />

          {/* Footer */}
          <Footer />
        </Box>
      </Box>
      {/* ─── Mobile Bottom Navigation ───────────────────────── */}
      {isMobile && <MobileBottomNav />}

      {/* ─── Remote Support Chat (shown to end users during active sessions) ─ */}
      <ActiveRemoteSession />
    </Box>
  );
}

export default memo(LayoutShell);
